import { useState, useEffect } from 'react';
import api from '../lib/api';

const DEFAULT_PRICING = {
  featured_post: 30,
  boost_post: 15,
  banner_price_3d: 35,
  banner_price_7d: 75,
  banner_price_14d: 130,
  banner_price_30d: 250,
  banner_max_slots: 5,
  // Feature flags gating the employer banner enticement surfaces (admin-configurable; default OFF)
  banner_show_my_banners: false,
  banner_show_job_create: false,
  banner_show_popup: false,
  banner_show_job_details: false,
  banner_popup_max_shows: 3,
  // Services Marketplace feature flag + reserved pricing keys (admin-configurable; default OFF / free)
  services_enabled: false,
  services_reveal_price: 0,
  services_featured_price: null
};

// ---------------------------------------------------------------------------
// Module-level cache + in-flight dedupe.
// A dashboard page mounts 7+ usePricing() consumers (TopBar, BottomNav, the
// Services sections, ...). Without this, each mounted its own useEffect and
// fired an identical GET /api/settings/pricing. Now: concurrent mounts share
// ONE request, and remounts within the TTL reuse the cached result entirely.
// Failures are NOT cached — the next mount retries.
// ---------------------------------------------------------------------------
const PRICING_TTL_MS = 60_000;
let cachedPricing = null;
let cachedAt = 0;
let inFlight = null;

function fetchPricingShared() {
  if (cachedPricing && (Date.now() - cachedAt) < PRICING_TTL_MS) {
    return Promise.resolve(cachedPricing);
  }
  if (inFlight) return inFlight;
  inFlight = api
    .get('/api/settings/pricing')
    .then((response) => {
      cachedPricing = response.data.pricing;
      cachedAt = Date.now();
      return cachedPricing;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

export const usePricing = () => {
  // Warm cache → render with real values immediately (loading=false, no flag flash).
  const [pricing, setPricing] = useState(() => cachedPricing || DEFAULT_PRICING);
  const [loading, setLoading] = useState(() => !cachedPricing);

  useEffect(() => {
    let mounted = true;
    fetchPricingShared()
      .then((p) => {
        if (mounted && p) setPricing(p);
      })
      .catch((error) => {
        console.error('Failed to fetch pricing:', error);
        // Use defaults if fetch fails (same behavior as before)
        if (mounted) setPricing(cachedPricing || DEFAULT_PRICING);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  return { pricing, loading };
};
